import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { PlusOutlined } from "@ant-design/icons";
import { message, Card, Button, Modal, Drawer, Form, Select } from "antd";
import { connect } from "dva";
import { useScreenDetector } from "@/utils/dom";
import { AssociatedUserType, RoleType, UserType, UserTypeToRoletype } from "@/utils/enum";
import { getUrlParams } from "@/utils/utils";
import { Validator } from "@/utils/validator";
import { addUserToEntityGroup } from "@/services/entityGroups";
import { getAllUsersRoles } from "@/services/roles";
import { createUser } from "@/services/users_new";
import NotFoundContent from "@/components/NotFoundContent";
import TableWithPagination from "@/components/TableWithPagination";
import UserForm from "@/forms/shared/UserForm";
import useUsersTableColumns from "./utils/useUsersTableColumns";

const { confirm } = Modal;
const { Option } = Select;

const UsersTable = ({
  dispatch,
  configLang,
  configPerm,
  singleEntity,
  entityGroup,
  transporterData,
  associatedUsers,
}) => {
  const newUserFormRef = useRef(null);

  const { isMobile } = useScreenDetector();

  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [loadingCreateUser, setLoadingCreateUser] = useState(false);

  const [loadingRoles, setLoadingRoles] = useState(false);
  const [roles, setRoles] = useState([]);

  const [associatedUsersPagination, setAssociatedUsersPagination] = useState({
    page: 1,
    pageSize: 10,
  });

  const { entityId, associateUserType, userType, entityName } = useMemo(() => {
    const entityGroupId = getUrlParams("entityGroupId");
    const dealerId = getUrlParams("dealerId");
    const transporterId = getUrlParams("transporterId");

    if (entityGroupId) {
      return {
        entityId: entityGroupId,
        associateUserType: AssociatedUserType.Group,
        userType: UserType.Group,
        entityName: entityGroup?.name || "",
      };
    } else if (dealerId) {
      return {
        entityId: dealerId,
        associateUserType: AssociatedUserType.Company,
        userType: UserType.Dealer,
        entityName: singleEntity?.data?.name || "",
      };
    } else if (transporterId) {
      return {
        entityId: transporterId,
        associateUserType: AssociatedUserType.Company,
        userType: UserType.Transporter,
        entityName: transporterData?.name || "",
      };
    }
  }, [entityGroup, singleEntity, transporterData]);

  const isDealerOrGroup = useMemo(() => [UserType.Dealer, UserType.Group].includes(userType), [userType]);

  const handleRemoveUserFromGroup = useCallback(
    user => {
      confirm({
        title: "Remove User",
        content: `Are you sure you want to remove ${user.userName} from ${entityName}?`,
        onOk: () => {
          const hide = message.loading("Removing user...", 0);
          dispatch({
            type: "entityUsers/removeUserFromEntity",
            userId: user.id,
          }).then(success => {
            hide();
            if (success) {
              message.success("Successfully removed user");
              fetchAssociatedUsers();
            } else {
              message.error("Error removing user");
            }
          });
        },
      });
    },
    [entityName, fetchAssociatedUsers],
  );

  const onUserMenuItemClick = useCallback(
    (key, user) => {
      if (key === "delete") {
        handleRemoveUserFromGroup(user);
      }
    },
    [handleRemoveUserFromGroup],
  );

  const columns = useUsersTableColumns({ configPerm, onUserMenuItemClick });

  const fetchAssociatedUsers = useCallback(() => {
    dispatch({
      type: "entityUsers/getAssociatedUsers",
      page: associatedUsersPagination.page,
      pageSize: associatedUsersPagination.pageSize,
      entityId,
    });
  }, [entityId, associatedUsersPagination]);

  const handleOpenAddUserModal = useCallback(() => {
    setAddUserModalOpen(true);
  }, []);

  const handleCloseAddUserModal = useCallback(() => {
    setAddUserModalOpen(false);
  }, []);

  const handleAddUserToGroup = useCallback(() => {
    newUserFormRef.current
      .getUserData()
      .then(userData => {
        userData.type = UserTypeToRoletype[userType];

        setLoadingCreateUser(true);

        createUser(userData)
          .then(createUserResponse => {
            if (!createUserResponse) {
              return;
            }

            const { id } = createUserResponse;
            const type = userType === UserType.Group ? AssociatedUserType.Group : AssociatedUserType.Company;

            addUserToEntityGroup(entityId, id, type)
              .then(() => {
                message.success("User created and associated successfully");
                newUserFormRef.current.resetFields();
                setAddUserModalOpen(false);
              })
              .finally(() => {
                fetchAssociatedUsers();
              });
          })
          .catch(error => {
            console.error(error);
          })
          .finally(() => {
            setLoadingCreateUser(false);
          });
      })
      .catch(() => {});
  }, [entityId, associateUserType, fetchAssociatedUsers]);

  useEffect(() => {
    fetchAssociatedUsers();
  }, [entityId, associatedUsersPagination]);

  useEffect(() => {
    if (!addUserModalOpen) {
      return;
    }

    setLoadingRoles(true);

    getAllUsersRoles(userType === UserType.Group ? RoleType.Dealer : userType)
      .then(response => {
        if (Array.isArray(response)) {
          setRoles(response);
        }
      })
      .finally(() => {
        setLoadingRoles(false);
      });
  }, [userType, addUserModalOpen]);

  return (
    <>
      <Card
        bordered={false}
        title={configLang.AbpIdentity.Users}
        style={{ height: "100%", marginRight: 20 }}
        headStyle={{ borderBottom: "unset" }}
        extra={[
          configPerm["CoreService.AssociateUsers.Create"] && (
            <Button
              key="add-user"
              icon={<PlusOutlined />}
              onClick={handleOpenAddUserModal}
              type="primary"
              style={{
                borderRadius: 10,
                color: "#fff",
              }}
            />
          ),
        ]}
      >
        <TableWithPagination
          loading={loadingCreateUser}
          page={associatedUsersPagination.page}
          pageSize={associatedUsersPagination.pageSize}
          dataSource={associatedUsers.items}
          totalItems={associatedUsers.totalCount}
          columns={columns}
          onPageChange={page =>
            setAssociatedUsersPagination({
              page,
              pageSize: associatedUsersPagination.pageSize,
            })
          }
          onPageSizeChange={pageSize =>
            setAssociatedUsersPagination({
              page: associatedUsersPagination.page,
              pageSize,
            })
          }
        />
      </Card>

      <Drawer
        title="Add User"
        visible={addUserModalOpen}
        width={isMobile ? "100%" : "40%"}
        onOk={handleAddUserToGroup}
        onClose={handleCloseAddUserModal}
        footer={
          <div style={{ textAlign: "right" }}>
            <Button onClick={handleCloseAddUserModal} style={{ marginRight: 8 }}>
              Cancel
            </Button>
            <Button onClick={handleAddUserToGroup} type="primary" loading={loadingCreateUser || loadingRoles}>
              Submit
            </Button>
          </div>
        }
      >
        {/* Form must be fully unmounted when drawer is closed, else chrome autofill bugs can trigger in the UserForm */}
        {addUserModalOpen && (
          <UserForm ref={newUserFormRef} isDealerOrGroup={isDealerOrGroup}>
            <Form.Item name="roles" label="Roles" rules={[Validator.required("Please select at least one role")]}>
              <Select
                mode="multiple"
                filterOption={(input, option) => option.key.toLowerCase().indexOf(input.toLowerCase()) !== -1}
                notFoundContent={NotFoundContent({
                  loading: loadingRoles,
                })}
              >
                {roles.map(({ id, name }) => (
                  <Option key={`${id}${name}`} value={name}>
                    {name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </UserForm>
        )}
      </Drawer>
    </>
  );
};

export default connect(({ configs, entityUsers, singleEntity, singleTransporter }) => ({
  configLang: configs.configLang,
  configPerm: configs.configPerm,
  associatedUsers: entityUsers.users,
  singleEntity,
  transporterData: singleTransporter?.data,
}))(UsersTable);
